import { type Reducer, useReducer } from 'react';
import type { Tag } from '@/models/user/tag';

export default function useTagReducer() {
  const [state, dispatch] = useReducer<TagReducer>((state, action) => tagReducer(state, action), {
    tags: [],
    adjustments: [],
  });

  return {
    ...state,
    dispatch,
    hasAdjustments: state.adjustments.length > 0,
    addedTags: state.adjustments.filter((a) => a.adjustment === 'add'),
    removedTags: state.adjustments.filter((a) => a.adjustment === 'remove'),
    toggleTag: (tag: Tag) => {
      if (state.adjustments.some((t) => t.Id === tag.Id)) {
        dispatch({
          action: 'reset-adjustment',
          tagId: tag.Id,
        });
      } else if (state.tags.some((t) => t.Id === tag.Id)) {
        dispatch({
          action: 'remove-tag',
          tag,
        });
      } else {
        dispatch({
          action: 'add-tag',
          tag,
        });
      }
    },
  };
}

type TagReducerAction =
  | {
      action: 'add-tag';
      tag: Tag;
    }
  | {
      action: 'remove-tag';
      tag: Tag;
    }
  | {
      action: 'reset-adjustment';
      tagId: number;
    }
  | {
      action: 'reset-adjustments';
    }
  | {
      action: 'set-current-tags';
      tags: Tag[];
    };

type TagReducerState = {
  tags: Tag[];
  adjustments: Array<Tag & { adjustment: 'add' | 'remove' }>;
};

type TagReducer = Reducer<TagReducerState, TagReducerAction>;

function tagReducer(state: TagReducerState, action: TagReducerAction): TagReducerState {
  switch (action.action) {
    case 'set-current-tags':
      return {
        ...state,
        tags: action.tags,
      };
    case 'reset-adjustment':
      return {
        ...state,
        adjustments: state.adjustments.filter((a) => a.Id !== action.tagId),
      };
    case 'reset-adjustments':
      return {
        ...state,
        adjustments: [],
      };
    case 'add-tag': {
      return {
        ...state,
        adjustments: state.adjustments
          .filter((adjustment) => adjustment.Id !== action.tag.Id)
          .concat([{ ...action.tag, adjustment: 'add' }]),
      };
    }
    case 'remove-tag': {
      return {
        ...state,
        adjustments: state.adjustments
          .filter((adjustment) => adjustment.Id !== action.tag.Id)
          .concat([{ ...action.tag, adjustment: 'remove' }]),
      };
    }
    default:
      return state;
  }
}
